import * as React from 'react';
import { Eye, Edit3, Save, X, ArrowLeft, Upload, RotateCcw, UserPlus } from 'lucide-react';
import { useFamilyStore } from '../../store/familyStore';
import { exportMockDBAsSeedCode, resetMockDB } from '../../lib/adapters/mock.adapter';
import { currentRoute, pushRoute } from '../../utils/routing';

interface ToolbarProps {
  onBackToDashboard?: () => void;
}

export const Toolbar: React.FC<ToolbarProps> = ({ onBackToDashboard }) => {
  const { viewMode, setViewMode, editMode, setEditMode, hasUnsavedChanges, setHasUnsavedChanges, isAdminView, members, stats } = useFamilyStore();

  const generations = Array.from({ length: stats.totalGenerations }, (_, i) => i + 1);

  const handleViewChange = (type: 'tree' | 'card' | 'list') => {
    setViewMode({ type });
  };

  const handleEditModeToggle = () => {
    if (editMode && hasUnsavedChanges) {
      if (confirm('You have unsaved changes. Are you sure you want to exit edit mode?')) {
        setEditMode(false);
      }
    } else {
      setEditMode(!editMode);
    }
  };

  const handlePublish = () => {
    const code = exportMockDBAsSeedCode();
    // Download as a .ts file the user can paste into mock.adapter.ts
    const blob = new Blob([code], { type: 'text/typescript' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'munshis-seed.ts';
    a.click();
    URL.revokeObjectURL(url);
    alert('Seed file downloaded. Replace the seed arrays in src/lib/adapters/mock.adapter.ts with this content, commit, and push to deploy.');
  };

  const handleReset = () => {
    if (confirm('Reset all data back to the original seed? This clears your locally saved edits.')) {
      resetMockDB();
    }
  };

  // Public viewers can suggest a new member for this tree.
  const treeId = currentRoute().match(/^\/family-tree\/([^/]+)/)?.[1];
  const handleSuggest = () => {
    if (treeId) pushRoute(`/submit/${treeId}`);
  };

  return (
    <div className="bg-white border-b border-gray-200 px-3 md:px-4 py-3">
      <div className="flex flex-wrap items-center gap-2 md:gap-0 md:space-x-6">
          {/* Back to Dashboard Button */}
          {onBackToDashboard && (
            <button
              onClick={onBackToDashboard}
              className="flex items-center space-x-2 text-blue-600 hover:text-blue-800 text-sm font-medium transition-colors"
            >
              <ArrowLeft className="w-4 h-4" />
              <span>Dashboard</span>
            </button>
          )}

          {/* Divider */}
          {onBackToDashboard && (
            <div className="h-6 w-px bg-gray-300" />
          )}

          {/* View Mode */}
          <div className="flex items-center space-x-2">
            <span className="text-sm font-medium text-gray-700">View:</span>
            <div className="flex rounded-lg border border-gray-300">
              <button
                onClick={() => handleViewChange('tree')}
                className={`px-3 py-1.5 text-sm rounded-l-lg transition-colors ${
                  viewMode.type === 'tree'
                    ? 'bg-blue-600 text-white'
                    : 'bg-white text-gray-700 hover:bg-gray-50'
                }`}
              >
                Tree
              </button>
              <button
                onClick={() => handleViewChange('card')}
                className={`px-3 py-1.5 text-sm border-l border-gray-300 transition-colors ${
                  viewMode.type === 'card'
                    ? 'bg-blue-600 text-white'
                    : 'bg-white text-gray-700 hover:bg-gray-50'
                }`}
              >
                Card
              </button>
              <button
                onClick={() => handleViewChange('list')}
                className={`px-3 py-1.5 text-sm rounded-r-lg border-l border-gray-300 transition-colors ${
                  viewMode.type === 'list'
                    ? 'bg-blue-600 text-white'
                    : 'bg-white text-gray-700 hover:bg-gray-50'
                }`}
              >
                List
              </button>
            </div>
          </div>

          {/* Edit Mode Toggle - Only for Tree View */}
          {viewMode.type === 'tree' && isAdminView && (
            <div className="flex items-center space-x-2">
              <span className="text-sm font-medium text-gray-700">Mode:</span>
              <button
                onClick={handleEditModeToggle}
                className={`flex items-center space-x-2 px-4 py-2 rounded-lg border-2 transition-all duration-200 ${
                  editMode
                    ? 'bg-blue-600 border-blue-600 text-white shadow-md'
                    : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-50'
                }`}
              >
                {editMode ? (
                  <>
                    <Edit3 className="w-4 h-4" />
                    <span className="font-medium">Edit Mode</span>
                    {hasUnsavedChanges && (
                      <div className="w-2 h-2 bg-yellow-400 rounded-full animate-pulse" />
                    )}
                  </>
                ) : (
                  <>
                    <Eye className="w-4 h-4" />
                    <span>View Mode</span>
                  </>
                )}
              </button>
            </div>
          )}

          {/* Filter */}
          <div className="flex items-center space-x-2">
            <span className="text-sm font-medium text-gray-700">Filter:</span>
            <select 
              className="px-3 py-1.5 text-sm border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
              value={viewMode.selectedGeneration ?? ''}
              onChange={(e) => setViewMode({ 
                selectedGeneration: e.target.value ? parseInt(e.target.value) : null 
              })}
            >
              <option value="">All Generations</option>
              {generations.map(gen => (
                <option key={gen} value={gen}>Generation {gen}</option>
              ))}
            </select>
            
            <label className="flex items-center space-x-2 text-sm">
              <input
                type="checkbox"
                checked={viewMode.showAlive}
                onChange={(e) => setViewMode({ showAlive: e.target.checked })}
                className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
              />
              <span>Living</span>
            </label>
            
            <label className="flex items-center space-x-2 text-sm">
              <input
                type="checkbox"
                checked={viewMode.showDeceased}
                onChange={(e) => setViewMode({ showDeceased: e.target.checked })}
                className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
              />
              <span>Deceased</span>
            </label>
          </div>

        {/* Suggest a member (public viewers) */}
        {!isAdminView && (
          <button
            onClick={handleSuggest}
            className="flex items-center space-x-1 px-3 py-1.5 bg-green-600 text-white rounded-md hover:bg-green-700 transition-colors text-sm ml-auto"
            title="Suggest a new member for this tree"
          >
            <UserPlus className="w-4 h-4" />
            <span>Suggest a Member</span>
          </button>
        )}

        {/* Edit Mode Actions */}
        {editMode && isAdminView && (
          <div className="flex items-center space-x-2 ml-auto">
            <button
              onClick={() => setHasUnsavedChanges(false)}
              className="flex items-center space-x-1 px-3 py-1.5 bg-green-600 text-white rounded-md hover:bg-green-700 transition-colors"
            >
              <Save className="w-4 h-4" />
              <span>Save All</span>
            </button>
            
            <button
              onClick={() => setEditMode(false)}
              className="flex items-center space-x-1 px-3 py-1.5 bg-gray-600 text-white rounded-md hover:bg-gray-700 transition-colors"
            >
              <X className="w-4 h-4" />
              <span>Exit Edit</span>
            </button>
          </div>
        )}

        {/* Data persistence actions - admin only */}
        {isAdminView && (
          <div className="flex items-center space-x-2 ml-auto">
            <button
              onClick={handlePublish}
              className="flex items-center space-x-1 px-3 py-1.5 bg-purple-600 text-white rounded-md hover:bg-purple-700 transition-colors text-sm"
              title="Download current data as seed code to commit"
            >
              <Upload className="w-4 h-4" />
              <span>Publish</span>
            </button>
            <button
              onClick={handleReset}
              className="flex items-center space-x-1 px-3 py-1.5 border border-gray-300 text-gray-600 rounded-md hover:bg-gray-50 transition-colors text-sm"
              title="Reset local data back to seed"
            >
              <RotateCcw className="w-4 h-4" />
              <span>Reset</span>
            </button>
          </div>
        )}
      </div>

      {/* Edit Mode Info Bar */}
      {editMode && isAdminView && (
        <div className="mt-3 p-3 bg-blue-50 border border-blue-200 rounded-lg">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
            <div className="flex items-center space-x-2">
              <Edit3 className="w-4 h-4 text-blue-600 shrink-0" />
              <span className="text-sm font-medium text-blue-800">
                Edit Mode Active - Click a card to edit, use the + icon to add a member
              </span>
            </div>
            <div className="text-xs text-blue-600">
              Tip: Click an empty area to exit edit mode
            </div>
          </div>
        </div>
      )}
    </div>
  );
};